import { useState, useMemo, useEffect } from 'react';
import React from 'react';
import { DependentSchema } from '@features/onboarding/schema/onboarding';
import { FieldErrors, UseFormReturn, Path } from 'react-hook-form';
import { DropdownFloatingSearch } from '@components/ui/dropdown-floating';

import { FormMessageSpan } from '@components/ui/form';

type DateKey = 'month' | 'day' | 'year';

const months = [
  { name: 'January', days: 31 },
  { name: 'February', days: 28 },
  { name: 'March', days: 31 },
  { name: 'April', days: 30 },
  { name: 'May', days: 31 },
  { name: 'June', days: 30 },
  { name: 'July', days: 31 },
  { name: 'August', days: 31 },
  { name: 'September', days: 30 },
  { name: 'October', days: 31 },
  { name: 'November', days: 30 },
  { name: 'December', days: 31 },
];

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const getMaxDaysInMonth = (monthName: string, year: number): number => {
  const monthObj = months.find((m) => m.name === monthName);
  if (!monthObj) return 31;
  if (monthName === 'February' && isLeapYear(year)) return 29;
  return monthObj.days;
};

export const ApplicantFormDatepicker = ({
  idxForm,
  form,
  errors,
  month,
  day,
  year,
  onChange,
  allowToday = false,
}: {
  idxForm: number;
  form: UseFormReturn<DependentSchema>;
  errors?: FieldErrors<DependentSchema['applicants'][number]['dateOfBirth']>;
  month: string;
  day: string;
  year: string;
  onChange: (key: DateKey, value: string) => void;
  /** When true (dependents), today is valid (0y). When false (myself/spouse), today cannot be selected. */
  allowToday?: boolean;
}) => {
  const [selectedMonth, setSelectedMonth] = useState(month);
  const [selectedDay, setSelectedDay] = useState(day);
  const [selectedYear, setSelectedYear] = useState(year);

  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();
  const currentDay = now.getUTCDate();

  const isFutureDate = (
    selectedYear: string,
    selectedMonth: string,
    selectedDay: string
  ): boolean => {
    const year = parseInt(selectedYear, 10);
    const monthIndex = months.findIndex((m) => m.name === selectedMonth);
    const day = parseInt(selectedDay, 10);

    if (Number.isNaN(year) || Number.isNaN(monthIndex) || Number.isNaN(day)) {
      return false;
    }

    if (year > currentYear) return true;
    if (year < currentYear) return false;

    if (monthIndex > currentMonth) return true;
    if (monthIndex < currentMonth) return false;

    return allowToday ? day > currentDay : day >= currentDay;
  };

  const availableMonths = useMemo(() => {
    if (parseInt(selectedYear, 10) === currentYear) {
      return months.slice(0, currentMonth + 1);
    }
    return months;
  }, [selectedYear, currentYear, currentMonth]);

  const availableDays = useMemo(() => {
    const monthObj = months.find((m) => m.name === selectedMonth);
    if (!monthObj) return [];

    let daysInMonth = monthObj.days;

    if (selectedMonth === 'February' && isLeapYear(parseInt(selectedYear))) {
      daysInMonth = 29;
    }

    if (
      parseInt(selectedYear, 10) === currentYear &&
      months.findIndex((m) => m.name === selectedMonth) === currentMonth
    ) {
      const maxDay = allowToday ? currentDay : currentDay - 1;
      if (maxDay < 1) return [];
      return Array.from({ length: daysInMonth }, (_, i) =>
        (i + 1).toString().padStart(2, '0')
      ).filter((d) => parseInt(d) <= maxDay);
    }

    return Array.from({ length: daysInMonth }, (_, i) =>
      (i + 1).toString().padStart(2, '0')
    );
  }, [
    selectedMonth,
    selectedYear,
    currentYear,
    currentMonth,
    currentDay,
    allowToday,
  ]);

  const years = useMemo(() => {
    return Array.from({ length: 101 }, (_, i) => (currentYear - i).toString());
  }, [currentYear]);

  function clampDayToMonth(
    monthName: string,
    year: number,
    day: string
  ): string {
    const maxDays = getMaxDaysInMonth(monthName, year);
    const dayNum = parseInt(day, 10);
    if (Number.isNaN(dayNum) || dayNum <= maxDays) return day;
    return maxDays.toString().padStart(2, '0');
  }

  function handleChange(key: DateKey, value: string) {
    const nextMonth = key === 'month' ? value : selectedMonth;
    const nextYear = key === 'year' ? value : selectedYear;
    const nextDay = key === 'day' ? value : selectedDay;

    let dayToSet = nextDay;
    if (nextMonth && nextYear && nextDay) {
      const yearNum = parseInt(nextYear, 10);
      if (!Number.isNaN(yearNum)) {
        const clamped = clampDayToMonth(nextMonth, yearNum, nextDay);
        if (clamped !== nextDay) {
          dayToSet = clamped;
        }
      }
    }

    if (key === 'month') {
      setSelectedMonth(value);
    }
    if (key === 'day') {
      setSelectedDay(value);
    }
    if (key === 'year') {
      setSelectedYear(value);
    }
    if (dayToSet !== nextDay) {
      setSelectedDay(dayToSet);
      onChange('day', dayToSet);
      form.setValue(`applicants.${idxForm}.dateOfBirth.day`, dayToSet);
    }

    onChange(key, value);
    form.trigger(
      `applicants.${idxForm}.dateOfBirth.${key}` as Path<DependentSchema>
    );
  }
  useEffect(() => {
    if (!selectedYear || !selectedMonth || !selectedDay) return;

    const yearNum = parseInt(selectedYear, 10);
    if (Number.isNaN(yearNum)) return;

    const clamped = clampDayToMonth(selectedMonth, yearNum, selectedDay);
    if (clamped !== selectedDay) {
      setSelectedDay(clamped);
      onChange('day', clamped);
      form.setValue(`applicants.${idxForm}.dateOfBirth.day`, clamped);
      return;
    }

    const isInvalid = isFutureDate(selectedYear, selectedMonth, selectedDay);
    if (isInvalid) {
      const monthIndex = months.findIndex((m) => m.name === selectedMonth);
      const isCurrentYear = yearNum === currentYear;
      const isCurrentMonth = monthIndex === currentMonth;

      let dayToSet: string;
      if (isCurrentYear && isCurrentMonth) {
        const maxDay = allowToday ? currentDay : currentDay - 1;
        dayToSet = maxDay >= 1 ? maxDay.toString().padStart(2, '0') : '';
      } else {
        dayToSet = '';
      }

      setSelectedDay(dayToSet);
      onChange('day', dayToSet);
      form.setValue(`applicants.${idxForm}.dateOfBirth.day`, dayToSet);
    }
  }, [
    selectedYear,
    selectedMonth,
    selectedDay,
    currentYear,
    currentMonth,
    currentDay,
    allowToday,
  ]);

  return (
    <div className="flex md:flex-row flex-col gap-3 w-full md:items-center max-md:gap-1">
      <span className="text-black text-xl flex items-center whitespace-nowrap lg:text-[22px] lg:tracking-tight h-13.5 lg:h-16.5 mb-auto">
        Date of Birth:
      </span>
      <div className="flex flex-row gap-4.5 w-full max-sm:gap-2">
        <div className="ml-2 max-md:ml-0 w-full flex flex-col">
          <DropdownFloatingSearch
            id={`month-select`}
            inputClass="border rounded-lg p-2 !text-[18px] md:!text-5 !h-13.5 xl:!h-16.5 w-full"
            chevronClass="max-xl:!-mr-1"
            placeholder="Select month"
            value={selectedMonth}
            onChange={(value) => handleChange('month', value)}
            options={availableMonths.map((m) => ({
              label: m.name,
              value: m.name,
            }))}
            label="Month"
            error={errors?.month && form.formState.isSubmitted}
          />
          {errors?.month && form.formState.isSubmitted && (
            <FormMessageSpan
              className="mt-2 text-sm text-center"
              text={errors.month.message}
            />
          )}
        </div>

        <div className="mdw-27 max-w-16 w-full min-w-[72px] flex flex-col">
          <DropdownFloatingSearch
            id={`day-select`}
            inputClass="border rounded-lg p-2 !text-[18px] md:!text-5 !h-13.5 xl:!h-16.5 w-full"
            chevronClass="max-xl:!-mr-1"
            placeholder="Select day"
            value={selectedDay}
            onChange={(value) => handleChange('day', value)}
            options={availableDays.map((d) => ({ label: d, value: d }))}
            label="Day"
            disabled={!availableDays.length}
            error={errors?.day && form.formState.isSubmitted}
          />
          {errors?.day && form.formState.isSubmitted && (
            <FormMessageSpan
              className="mt-2 text-sm text-center"
              text={errors.day.message}
            />
          )}
        </div>

        <div className="md:w-29 max-w-20 w-full min-w-20 flex flex-col">
          <DropdownFloatingSearch
            id={`year-select`}
            inputClass="border rounded-lg p-2 !text-[18px] md:!text-5 !h-13.5 xl:!h-16.5 w-full"
            chevronClass="max-xl:!-mr-1"
            placeholder="Select year"
            value={selectedYear}
            onChange={(value) => handleChange('year', value)}
            options={years.map((y) => ({ label: y, value: y }))}
            label="Year"
            error={errors?.year && form.formState.isSubmitted}
          />
          {errors?.year && form.formState.isSubmitted && (
            <FormMessageSpan
              className="mt-2 text-sm text-center"
              text={errors.year.message}
            />
          )}
        </div>
      </div>
    </div>
  );
};
